import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { IDispatchManufactoring } from "../models/dispatchManufactoring";
import { IDispatchManufactoringApiResponse } from "../models/api/dispatchManufactoringApiResponse";
import { IDispatchManufactoringForm } from "../models/forms/dispatchManufactoringForm";
import { IDispatchManufactoringFilter } from "../models/filters/dispatchManufactoringFilter";
import DispatchManufactoringProductionServices from "../services/DispatchManufactoringService";
import { IPrevisionalDispatch } from "../models/previsionalDispatch";
import { IParameter } from "../models/parameter";
import { IManufactoringProduction } from "../models/manufactoringProduction";
import { ICenter } from "../models/center";
import { ILot } from "../models/lot";
import { GroupedStock } from "../helper/type";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { useCenterStore } from "./center.store";
import { useParameterStore } from "./parameter.store";
import { useLotStore } from "./lot.store";
import { useManufactoringProductionStore } from "./manufactoringProduction.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";

export const useDispatchManufactoringStore = defineStore("DispatchManufactoring", () => {
    // State
    const isLoading = ref<boolean>(false);
    const datas = ref<IDispatchManufactoring[]>([]);
    const current = ref<IDispatchManufactoring>({} as IDispatchManufactoring);
    const itemForm = ref<IDispatchManufactoringForm>({} as IDispatchManufactoringForm);
    const filter = ref<IDispatchManufactoringFilter>({} as IDispatchManufactoringFilter);
    const recentOnly = ref<boolean>(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    // true si datas a été chargé via loadEjaculateData (composite), false si via load() direct
    const loadedViaComposite = ref(false);

    // Données prévisionnelles pré-résolues par le backend (pas d'hydratation)
    const previsionalDatas = ref<IPrevisionalDispatch[]>([]);
    const previsionalLoadedCenterId = ref<number | undefined>(undefined);

    // Compteur incrémenté après chaque batch SignalR — permet aux computed externes
    // de forcer une réévaluation quand les dispatches changent via SignalR
    const signalRVersion = ref(0);

    // Actions - Hydration
    /**
     * Hydrate DispatchManufactorings from API response
     */
    async function hydrateDispatchManufactorings(dispatches: IDispatchManufactoringApiResponse[], centerId?: number, dateFrom?: Date): Promise<IDispatchManufactoring[]> {
        if (dispatches.length === 0) return [];

        // 1. Get stores
        const centerStore = useCenterStore();
        const parameterStore = useParameterStore();
        const lotStore = useLotStore();
        const manufactoringProductionStore = useManufactoringProductionStore();

        // 2. Load in parallel if needed
        // Si un store est en cours de chargement, attendre qu'il finisse au lieu de skip
        const waitForStore = (store: { isLoading: boolean }, timeoutMs = 10000) =>
            new Promise<void>((resolve, reject) => {
                if (!store.isLoading) return resolve();
                const start = Date.now();
                const interval = setInterval(() => {
                    if (!store.isLoading) { clearInterval(interval); resolve(); }
                    else if (Date.now() - start > timeoutMs) { clearInterval(interval); resolve(); }
                }, 50);
            });

        const loadPromises: Promise<unknown>[] = [];
        if (centerStore.datas.length === 0) {
            loadPromises.push(centerStore.load({ page: 1, pageSize: 0 }));
        }
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        const lotNeedsLoad =
            lotStore.datas.length === 0 ||
            (dateFrom === undefined && lotStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && lotStore.loadedDateFrom !== undefined && lotStore.loadedDateFrom > dateFrom);
        if (lotNeedsLoad) {
            if (lotStore.isLoading) {
                loadPromises.push(waitForStore(lotStore));
            } else {
                loadPromises.push(lotStore.load({ page: 1, pageSize: 0, centerId, dateFrom }, centerId));
            }
        }
        const mfpNeedsLoad =
            manufactoringProductionStore.datas.length === 0 ||
            (dateFrom === undefined && manufactoringProductionStore.loadedDateFrom !== undefined) ||
            (dateFrom !== undefined && manufactoringProductionStore.loadedDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom > dateFrom);
        if (mfpNeedsLoad) {
            if (manufactoringProductionStore.isLoading) {
                loadPromises.push(waitForStore(manufactoringProductionStore));
            } else {
                loadPromises.push(manufactoringProductionStore.load({ page: 1, pageSize: 0, centerId, dateFrom }));
            }
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // 3. Create Maps for O(1) lookups
        const centerMap = new Map(centerStore.datas.map(c => [c.id, c]));
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const lotMap = new Map(lotStore.datas.map(l => [l.id, l]));
        const manufactoringProductionMap = new Map(manufactoringProductionStore.datas.map(m => [m.id, m]));
        const dispatchMap = new Map<number, IDispatchManufactoring>();

        // Charger par id les Lots manquants (lot hétérospermie créé sur un autre poste et reçu via
        // SignalR : le lotStore déjà chargé ne contient pas ce nouveau lot → belongsToLot serait null
        // → le dispatch serait exclu du suivi de fabrication, invisible jusqu'au F5). Mantis 0028085.
        const missingLotIds = [...new Set(
            dispatches
                .flatMap(d => [d.belongsToLotId, d.partOfLotId])
                .filter((id): id is number => !!id && !lotMap.has(id))
        )];
        if (missingLotIds.length > 0) {
            const loaded = await Promise.all(missingLotIds.map(id => lotStore.ensureLoadedById(id)));
            loaded
                .filter((l): l is ILot => l !== undefined)
                .forEach(l => lotMap.set(l.id, l));
        }

        // 4. First pass: create all entities without children
        const models: IDispatchManufactoring[] = [];
        for (const dispatch of dispatches) {
            const model = { ...dispatch } as unknown as IDispatchManufactoring;

            // Link references (O(1) via Map)
            model.center = centerMap.get(dispatch.centerId) ?? model.center;
            model.packaging = parameterMap.get(dispatch.packagingId) ?? model.packaging;
            model.belongsToLot = dispatch.belongsToLotId ? lotMap.get(dispatch.belongsToLotId) : undefined;
            model.partOfLot = dispatch.partOfLotId ? lotMap.get(dispatch.partOfLotId) : undefined;
            model.manufactoringProduction = dispatch.manufactoringProductionId ? manufactoringProductionMap.get(dispatch.manufactoringProductionId) : undefined;

            // Initialize children array
            model.children = [];

            models.push(model);
            dispatchMap.set(model.id, model);
        }

        // 5. Second pass: link parent/children relationships
        // model.parentId est déjà disponible via le spread ({ ...dispatch }) — pas besoin de dispatches.find() (O(n²))
        for (const model of models) {
            if (model.parentId) {
                model.parent = dispatchMap.get(model.parentId);
                if (model.parent) {
                    model.parent.children.push(model);
                }
            }
        }

        return models;
    }

    // Getters
    const getBelongsToLot = computed(() => {
        return (lotId: number): IDispatchManufactoring[] => {
            return datas.value.filter(d => d.belongsToLot?.id == lotId && d.isCanceled == false && d.isValidated == false);
        };
    });

    const getPartOfLot = computed(() => {
        return (lotId: number): IDispatchManufactoring[] => {
            return datas.value.filter(d => d.partOfLot?.id == lotId && d.isCanceled == false && d.isValidated == false);
        };
    });

    const getVolumeFabricationForProduction = computed(() => {
        return (lotId: number): number => {
            let volume = 0;
            const matching = datas.value.filter(d => d.belongsToLot?.id == lotId && d.isCanceled == false);
            matching.forEach(dispatchFabrication => {
                if (dispatchFabrication.volume > 0 && dispatchFabrication.numberDosesTheoretical > 0) {
                    volume += (dispatchFabrication.numberDosesTheoretical * dispatchFabrication.volume) + dispatchFabrication.primerVolume;
                }
            });
            return volume;
        };
    });

    const getVolumeFabricationForLot = computed(() => {
        return (lotId: number): number => {
            let volume = 0;
            datas.value.forEach(dispatchFabrication => {
                if (dispatchFabrication.partOfLot?.id == lotId && dispatchFabrication.volume > 0 && dispatchFabrication.numberDosesTheoretical > 0 && dispatchFabrication.isCanceled == false) {
                    volume += (dispatchFabrication.numberDosesTheoretical * dispatchFabrication.volume) + dispatchFabrication.primerVolume;
                }
            });
            return volume;
        };
    });

    const getPrevisionalStockProduction = computed(() => {
        return (idProduction: number, packagingCode: string, volumeDose: number, armada: boolean): number => {
            const dispatches = datas.value.filter((dispatch) => {
                const baseCondition = (
                    dispatch.manufactoringProduction?.production?.id === idProduction &&
                    dispatch.packaging?.code3 === packagingCode &&
                    dispatch.volume === volumeDose &&
                    dispatch.belongsToLot?.armada === armada &&
                    dispatch.isCanceled === false &&
                    dispatch.isValidated === false
                );

                const isValidForHetero = packagingCode === 'H'
                    ? !dispatch.partOfLot
                    : true;

                return baseCondition && isValidForHetero;
            });

            const stockPrevisionnel = dispatches.reduce((total, dispatch) => {
                return total - ((dispatch.numberDosesTheoretical || 0));
            }, 0);

            return stockPrevisionnel;
        };
    });

    // --- Getters prévisionnels : lisent depuis previsionalDatas (champs plats, pas d'hydratation) ---

    const getDispatchPrevisionnelHetero = computed(() => {
        return (schema: string, armada: boolean, volumeDose: number): GroupedStock[] => {
            return previsionalDatas.value
                .filter(d =>
                    d.geneticTypeCode === schema &&
                    d.volume === volumeDose &&
                    d.isArmada === armada &&
                    !d.hasPartOfLot &&
                    !d.hasManufactoringProduction
                )
                .map(d => ({
                    codeArticle: d.geneticTypeCode,
                    idArticle: 0,
                    libelleArticle: d.articleName,
                    centerCode: d.centerCode,
                    isArmada: d.isArmada,
                    isHeterospermie: d.isHeterospermie,
                    conditionnement: d.packagingCode,
                    conditionnementCode: d.packagingCode,
                    schemaCode: d.geneticTypeCode,
                    quantity: -(d.numberDosesTheoretical || 0),
                    volumeDose: d.volume,
                    spzDose: d.spzDose || 0,
                    stockUnity: {} as GroupedStock["stockUnity"],
                    stocks: [] as GroupedStock["stocks"],
                    isPrevisional: true
                }));
        };
    });

    const getDispatchPrevisionnal = computed(() => {
        return (schema: string, packagingCode: string, volumeDose: number, armada: boolean, isHetero: boolean): GroupedStock[] => {
            return previsionalDatas.value
                .filter(d => {
                    const baseCondition =
                        d.geneticTypeCode === schema &&
                        (packagingCode === '' || d.packagingCode === packagingCode) &&
                        d.volume === volumeDose &&
                        d.isArmada === armada &&
                        !d.hasPartOfLot;

                    const isValidForHetero = isHetero ? d.isHeterospermie : true;
                    const isValidForMono = !isHetero && packagingCode !== ''
                        ? (d.hasManufactoringProduction || d.hasBelongsToLot)
                        : true;
                    const isValidHeteroMono = packagingCode === ''
                        ? (d.hasManufactoringProduction || d.hasBelongsToLot)
                        : true;

                    return baseCondition && isValidForHetero && isValidForMono && isValidHeteroMono;
                })
                .map(d => {
                    const dosesDisponibles = d.volume > 0
                        ? Math.floor((d.volumeAvailable ?? 0) / d.volume)
                        : 0;

                    return {
                        codeArticle: d.geneticTypeCode,
                        idArticle: 0,
                        libelleArticle: d.articleName,
                        centerCode: d.centerCode,
                        isArmada: d.isArmada,
                        isHeterospermie: d.isHeterospermie,
                        conditionnement: d.packagingCode,
                        conditionnementCode: d.packagingCode,
                        schemaCode: d.geneticTypeCode,
                        quantity: -dosesDisponibles,
                        volumeDose: d.volume,
                        spzDose: d.spzDose || 0,
                        stockUnity: {} as GroupedStock["stockUnity"],
                        stocks: [] as GroupedStock["stocks"],
                        isPrevisional: true
                    };
                });
        };
    });

    const getPrevisionalStock = computed(() => {
        return (codeArticle: string, packagingCode: string, volumeDose: number, armada: boolean, isHetero: boolean): number => {
            const dispatches = previsionalDatas.value.filter(d =>
                d.geneticTypeCode === codeArticle &&
                d.packagingCode === packagingCode &&
                d.volume === volumeDose &&
                d.isArmada === armada &&
                (!isHetero ? d.hasManufactoringProduction : true)
            );

            return dispatches.reduce((total, d) => total - (d.numberDosesTheoretical || 0), 0);
        };
    });

    const getListeFabricationToDispatch = computed((): IDispatchManufactoring[] => {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 7);

        const normalizeDateMs = (date?: string | Date | null): number => {
            if (!date) return 0;
            const d = new Date(date);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
        };

        const todayMs = today.getTime();
        const sevenDaysAgoMs = sevenDaysAgo.getTime();

        const isWithinLast7Days = (dateMs: number): boolean => {
            return dateMs >= sevenDaysAgoMs && dateMs <= todayMs;
        };

        // Passe O(n) : filtre + pré-calcul des clés de tri en une seule fois
        type WithSortKey = { item: IDispatchManufactoring; dateMs: number; name: string };
        const filtered: WithSortKey[] = [];
        for (const fabrication of datas.value) {
            let keep: boolean;
            if (recentOnly.value) {
                const validatedMs = normalizeDateMs(fabrication.dateValidation);
                keep = fabrication.isValidated && isWithinLast7Days(validatedMs);
            } else {
                keep = fabrication.packaging?.code3 !== 'H'
                    && !fabrication.isCanceled
                    && !fabrication.isValidated
                    // Exclure les contributions hétéro (partOfLotId sans manufactoringProduction ni belongsToLot)
                    && (fabrication.manufactoringProduction != null || fabrication.belongsToLot != null);
            }
            if (!keep) continue;

            filtered.push({
                item: fabrication,
                dateMs: normalizeDateMs(fabrication.manufactoringProduction?.production?.dateProduction),
                name: fabrication.manufactoringProduction?.production?.animal?.name?.toUpperCase() || '',
            });
        }

        // Tri O(n log n) sans recalcul de date/nom dans le comparateur
        filtered.sort((a, b) => {
            if (a.dateMs !== b.dateMs) return b.dateMs - a.dateMs;
            return a.name.localeCompare(b.name);
        });

        return filtered.map(x => x.item);
    });

    const getByManufactoringProductionId = computed((): IDispatchManufactoring[] => {
        return datas.value.filter((dispatch) => dispatch.manufactoringProduction?.id === filter.value.manufactoringProductionId && dispatch.isCanceled == false && dispatch.isValidated == false);
    });

    const getDispatchBelongingToLot = computed(() => {
        return (lotId: number) => {
            return datas.value.filter(d => d.partOfLot?.id == lotId);
        };
    });

    // Actions
    async function resendStock(centerCodeSpecies: string) {
        isLoading.value = true;
        let result = await DispatchManufactoringProductionServices.resendStock(centerCodeSpecies);
        isLoading.value = false;
        datas.value = [];
        return result;
    }

    async function load(loadFilter: IDispatchManufactoringFilter, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);

        filter.value = loadFilter;
        isLoading.value = true;

        // Pré-chargement des sous-stores en parallèle avec l'appel API principal.
        // lot et manufactoringProduction sont lazy-loaded (absents du storeLoader) :
        // sans ça, hydrateDispatchManufactorings() les chargerait séquentiellement après le GET.
        // Guard isLoading : évite le double-chargement si la vue charge déjà ces stores en parallèle.
        const lotStore = useLotStore();
        const manufactoringProductionStore = useManufactoringProductionStore();
        const subStorePromises: Promise<unknown>[] = [];
        const reqDateFrom = loadFilter.dateFrom;
        const lotNeedsLoad =
            lotStore.datas.length === 0 ||
            lotStore.loadedCenterId !== loadFilter.centerId ||
            (reqDateFrom === undefined && lotStore.loadedDateFrom !== undefined) ||
            (reqDateFrom !== undefined && lotStore.loadedDateFrom !== undefined && lotStore.loadedDateFrom > reqDateFrom);
        if (lotNeedsLoad && !lotStore.isLoading) {
            subStorePromises.push(lotStore.load({ page: 1, pageSize: 0, centerId: loadFilter.centerId, dateFrom: reqDateFrom }, loadFilter.centerId));
        }
        const mfpNeedsLoad =
            manufactoringProductionStore.datas.length === 0 ||
            (reqDateFrom === undefined && manufactoringProductionStore.loadedDateFrom !== undefined) ||
            (reqDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom !== undefined && manufactoringProductionStore.loadedDateFrom > reqDateFrom);
        if (mfpNeedsLoad && !manufactoringProductionStore.isLoading) {
            subStorePromises.push(manufactoringProductionStore.load({ page: 1, pageSize: 0, centerId: loadFilter.centerId, dateFrom: reqDateFrom }));
        }

        start('GET /DispatchManufactoring (réseau)');
        const result = await DispatchManufactoringProductionServices.getAll<IDispatchManufactoringFilter, IDispatchManufactoringApiResponse>(loadFilter);
        end('GET /DispatchManufactoring (réseau)', 'api');

        // Attendre que les sous-stores soient prêts (généralement déjà terminés pendant le GET)
        if (subStorePromises.length > 0) {
            await Promise.all(subStorePromises);
        }

        // On remplace datas uniquement après hydratation pour éviter le flash visuel
        // (ancien : datas = [] avant l'await → grille brièvement vide à chaque rechargement)
        if (!result.error) {
            start('hydrateDispatchManufactorings()');
            datas.value = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadFilter.centerId, loadFilter.dateFrom);
            end('hydrateDispatchManufactorings()', 'mapping');
        } else {
            datas.value = [];
        }
        loadedCenterId.value = loadFilter.centerId;
        loadedViaComposite.value = false;
        isLoading.value = false;
        return result;
    }

    async function loadPrevisional(centerId: number) {
        const result = await DispatchManufactoringProductionServices.getPrevisionalStock(centerId);
        previsionalDatas.value = result.data ?? [];
        previsionalLoadedCenterId.value = centerId;
    }

    async function get(id: number) {
        const result = await DispatchManufactoringProductionServices.getById<IDispatchManufactoringApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse]);

            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function createDispatchToDeleteLot(lot: ILot, volumeRestant: number, centerId: number, volumeDose = 40, packagingDefaultId = 0) {
        try {
            if (!lot || volumeRestant <= 0) {
                throw new Error(`Lot ${lot.id} introuvable ou volume restant invalide`);
            }

            const nombreDosesNecessaires = Math.ceil(volumeRestant / volumeDose);

            const dispatchForm: IDispatchManufactoringForm = {
                id: 0,
                volume: volumeDose,
                parentId: undefined,
                manufactoringProductionId: undefined,
                packagingId: packagingDefaultId,
                numberDosesManufactured: nombreDosesNecessaires,
                numberDosesTheoretical: nombreDosesNecessaires,
                numberManufactoring: -1,
                dateManufactoring: new Date(),
                isArmada: lot.armada || false,
                datePackaging: new Date(),
                isValidated: true,
                isCanceled: false,
                belongsToLotId: lot.id,
                partOfLotId: undefined,
                primerVolume: 0,
                centerId: centerId || 0,
                archived: false,
            };

            itemForm.value = dispatchForm;
            const result = await save();
            return result;

        } catch (error) {
            console.error(`Erreur lors de la création du dispatch de suppression pour le lot ${lot.id}:`, error);

            const errorResult = new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(
                true,
                { errors: new DataOperationError() }
            );
            return errorResult;
        }
    }

    /**
     * Crée en batch les dispatches de suppression pour une liste de lots.
     * Envoie une seule requête HTTP au lieu d'une boucle séquentielle,
     * ce qui évite les mises à jour SignalR intermédiaires et les conflits sur itemForm partagé.
     */
    async function batchCreateDispatchesToDeleteLots(lots: { lot: ILot, volume: number }[], centerId: number, volumeDose = 40, packagingDefaultId = 0) {
        const forms: IDispatchManufactoringForm[] = lots
            .filter(({ lot, volume }) => lot && volume > 0)
            .map(({ lot, volume }) => {
                const nombreDosesNecessaires = Math.ceil(volume / volumeDose);
                return {
                    id: 0,
                    volume: volumeDose,
                    parentId: undefined,
                    manufactoringProductionId: undefined,
                    packagingId: packagingDefaultId,
                    numberDosesManufactured: nombreDosesNecessaires,
                    numberDosesTheoretical: nombreDosesNecessaires,
                    numberManufactoring: -1,
                    dateManufactoring: new Date(),
                    isArmada: lot.armada || false,
                    datePackaging: new Date(),
                    isValidated: true,
                    isCanceled: false,
                    belongsToLotId: lot.id,
                    partOfLotId: undefined,
                    primerVolume: 0,
                    centerId: centerId || 0,
                    archived: false,
                } as IDispatchManufactoringForm;
            });

        if (forms.length === 0) return;
        return await batchSave(forms);
    }

    /**
     * Sauvegarde un dispatch à partir d'un formulaire passé en paramètre.
     * Contrairement à save(), n'utilise pas l'état partagé itemForm/current,
     * ce qui permet d'appeler cette méthode en parallèle sans race condition.
     */
    async function saveDispatch(form: IDispatchManufactoringForm) {
        const lotStore = useLotStore();
        const result = await DispatchManufactoringProductionServices.update(form);
        if (!result.error) {
            const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
            const index = datas.value.findIndex((item) => item.id === form.id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
        }
        return result;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        const lotStore = useLotStore();
        let result: ServiceOperationResult<IDispatchManufactoring | IDataOperationResult<IDispatchManufactoring>>;
        if (itemForm.value.id > 0) {
            result = await DispatchManufactoringProductionServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
                current.value = modelData;

                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(true, { errors: error });
                }
            }
        } else {
            result = await DispatchManufactoringProductionServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateDispatchManufactorings([result.data as IDispatchManufactoringApiResponse], loadedCenterId.value, lotStore.loadedDateFrom);
                current.value = modelData;
            }
        }
        return result;
    }

    async function batchSave(forms: IDispatchManufactoringForm[]) {
        const lotStore = useLotStore();
        const created = forms.filter(f => f.id == 0);
        const updated = forms.filter(f => f.id > 0);

        const results: IDispatchManufactoring[] = [];

        if (created.length > 0) {
            const result = await DispatchManufactoringProductionServices.batchCreate(created);
            if (result.error) return;

            const hydratedData = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadedCenterId.value, lotStore.loadedDateFrom);
            for (const dm of hydratedData) {
                // SignalR peut avoir déjà ajouté l'item avant la réponse HTTP (race condition)
                if (datas.value.findIndex((a) => a.id == dm.id) == -1) {
                    datas.value.push(dm);
                }
                results.push(dm);
            }
        }

        if (updated.length > 0) {
            const result = await DispatchManufactoringProductionServices.batchUpdate(updated);
            if (result.error) return;

            const hydratedData = await hydrateDispatchManufactorings(result.data as IDispatchManufactoringApiResponse[], loadedCenterId.value, lotStore.loadedDateFrom);
            for (const dm of hydratedData) {
                const existingIndex = datas.value.findIndex(edm => edm.id == dm.id);

                if (existingIndex == -1) {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = "Item non trouvé dans le store";
                    return new ServiceOperationResult<IDataOperationResult<IDispatchManufactoring>>(true, { errors: error });
                }

                datas.value[existingIndex] = dm;
                results.push(dm);
            }
        }

        return results;
    }

    function toForm(item: IDispatchManufactoring): IDispatchManufactoringForm {
        const form = {} as IDispatchManufactoringForm;
        Object.assign(form, item);
        form.manufactoringProductionId = item.manufactoringProductionId;
        form.packagingId = item.packagingId;
        form.belongsToLotId = item.belongsToLotId;
        form.partOfLotId = item.partOfLotId;
        form.parentId = item.parentId;
        form.centerId = item.centerId;
        return form;
    }

    function newForm(lienId?: number): IDispatchManufactoringForm {
        itemForm.value = {
            id: 0,
            volume: 0,
            parentId: undefined,
            manufactoringProductionId: lienId,
            packagingId: 0,
            numberDosesManufactured: 0,
            numberDosesTheoretical: 0,
            numberManufactoring: 0,
            dateManufactoring: new Date(),
            isArmada: false,
            datePackaging: new Date(),
            isValidated: false,
            isCanceled: false,
            belongsToLotId: undefined,
            partOfLotId: undefined,
            primerVolume: 0,
            centerId: 0,
            archived: false,
        } as IDispatchManufactoringForm;
        return itemForm.value;
    }

    function newItem(lien: IManufactoringProduction | undefined): IDispatchManufactoring {
        current.value = {
            id: 0,
            parent: undefined,
            children: [],
            manufactoringProduction: lien,
            manufactoringProductionId: lien?.id,
            volume: 0,
            numberDosesManufactured: 0,
            numberDosesTheoretical: 0,
            numberManufactoring: 0,
            packaging: { id: 0 } as IParameter,
            packagingId: 0,
            datePackaging: new Date(),
            dateManufactoring: new Date(),
            isValidated: false,
            isCanceled: false,
            belongsToLot: undefined,
            partOfLot: undefined,
            isArmada: false,
            primerVolume: 0,
            center: { id: 0 } as ICenter,
            centerId: 0,
            archived: false,
            volumeAvailable: 0,
        } as IDispatchManufactoring;
        return current.value;
    }

    return {
        // State
        isLoading,
        datas,
        current,
        itemForm,
        filter,
        recentOnly,
        loadedCenterId,
        loadedViaComposite,
        previsionalDatas,
        previsionalLoadedCenterId,
        signalRVersion,
        // Getters
        getBelongsToLot,
        getPartOfLot,
        getVolumeFabricationForProduction,
        getVolumeFabricationForLot,
        getPrevisionalStockProduction,
        getDispatchPrevisionnelHetero,
        getDispatchPrevisionnal,
        getPrevisionalStock,
        getListeFabricationToDispatch,
        getByManufactoringProductionId,
        getDispatchBelongingToLot,
        // Actions
        hydrateDispatchManufactorings,
        resendStock,
        load,
        loadPrevisional,
        get,
        searchStoreById,
        createDispatchToDeleteLot,
        batchCreateDispatchesToDeleteLots,
        saveDispatch,
        save,
        batchSave,
        toForm,
        newForm,
        newItem
    };
});

// Batch les événements SignalR pour éviter N hydratations + N recalculs de computed
// quand orient-hetero crée plusieurs dispatches d'un coup
let signalRPendingPayloads: IPayloadStore[] = [];
let signalRFlushTimer: ReturnType<typeof setTimeout> | null = null;

async function flushSignalRBatch() {
    const payloads = signalRPendingPayloads;
    signalRPendingPayloads = [];
    signalRFlushTimer = null;

    if (payloads.length === 0) return;

    const store = useDispatchManufactoringStore();
    const lotStore = useLotStore();
    const centerId = store.loadedCenterId;
    const dateFrom = lotStore.loadedDateFrom;

    // Séparer creates, updates, deletes
    const creates: IDispatchManufactoringApiResponse[] = [];
    const updates: IDispatchManufactoringApiResponse[] = [];
    const deleteIds: number[] = [];

    for (const payload of payloads) {
        switch (payload.action) {
            case "Create":
                if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1)
                    creates.push(payload.data as IDispatchManufactoringApiResponse);
                break;
            case "Update":
                if (store.datas.findIndex((a) => a.id == payload.data?.id) > -1)
                    updates.push(payload.data as IDispatchManufactoringApiResponse);
                else
                    // Dispatch pas dans le store mais du même centre → l'ajouter (ex: renvoi depuis un autre onglet)
                    creates.push(payload.data as IDispatchManufactoringApiResponse);
                break;
            case "Delete":
                if (payload.id) deleteIds.push(payload.id);
                break;
        }
    }

    // Hydrater en batch (1 seul appel au lieu de N)
    if (creates.length > 0) {
        const hydrated = await store.hydrateDispatchManufactorings(creates, centerId, dateFrom);
        for (const dm of hydrated) {
            if (store.datas.findIndex((a) => a.id == dm.id) == -1)
                store.datas.push(dm);
        }
    }

    if (updates.length > 0) {
        const hydrated = await store.hydrateDispatchManufactorings(updates, centerId, dateFrom);
        for (const dm of hydrated) {
            const idx = store.datas.findIndex((a) => a.id == dm.id);
            if (idx > -1) store.datas.splice(idx, 1, dm);
        }
    }

    for (const id of deleteIds) {
        const idx = store.datas.findIndex((a) => a.id == id);
        if (idx > -1) store.datas.splice(idx, 1);
    }

    // Recharger les données prévisionnelles (projection SQL légère)
    if (store.previsionalLoadedCenterId) {
        await store.loadPrevisional(store.previsionalLoadedCenterId);
    }

    // Incrémenter le compteur pour forcer la réévaluation des computed externes
    // (ex: computedRowData dans EjaculateListView)
    store.signalRVersion++;
}

signalRManager.connection.on("MajDispatchManufactoring", async (payload: IPayloadStore) => {
    const store = useDispatchManufactoringStore();
    // Ignorer les événements d'autres centres
    if (store.loadedCenterId !== undefined && payload.data?.centerId !== undefined
        && payload.data.centerId !== store.loadedCenterId) {
        return;
    }

    signalRPendingPayloads.push(payload);
    if (signalRFlushTimer) clearTimeout(signalRFlushTimer);
    signalRFlushTimer = setTimeout(flushSignalRBatch, 200);
});
